import { useNavigate } from "react-router-dom";
import { Row, Col } from "react-bootstrap";
import Stats from "../../mycomp/Dashboard/Doctor/Stats";
import PerformanceOfAgents from "../../mycomp/Tables/BasicTable/PerformanceOfAgents";
import { useContext, useMemo, useState, useEffect } from "react";
import { AuthContext } from "../../context/AuthContext";
import { fetchWorkspaceReports, fetchWorkspaces } from "../../api/api";

// Helper to convert decimal hours to HH:MM:SS
const hoursToHMS = (decimalHours) => {
  const totalSeconds = Math.round(decimalHours * 3600);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${hours.toString().padStart(2,"0")}:${minutes.toString().padStart(2,"0")}:${seconds.toString().padStart(2,"0")}`;
};

// Helper to get productive seconds
const timeToSeconds = (timeString) => {
  if (!timeString) return 0;
  const [h, m, s] = timeString.split(':').map(Number);
  return h * 3600 + m * 60 + (s || 0);
};
const getProductiveSeconds = (workedTime, breakTime, idleTime) => {
  return Math.max(0, timeToSeconds(workedTime) - timeToSeconds(breakTime) - timeToSeconds(idleTime));
};

const Reports = () => {
  const { user } = useContext(AuthContext);
  const [allReports, setAllReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [viewMode] = useState('summary');
  const [selectedEmployee] = useState(null);
  const navigate = useNavigate();


  const fetchReports = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const workspaces = await fetchWorkspaces(user.id);
      const adminWorkspaces = workspaces.filter(w => w.owner_id === user.id);
      const reportsResults = await Promise.all(
        adminWorkspaces.map(w => fetchWorkspaceReports(w.id))
      );

      const flattened = reportsResults.flat().map(r => ({
        user_id: r.user_id,
        name: r.employee_name,
        email: r.employee_email,
        workedHours: hoursToHMS(+r.worked_hours || 0),
        breakTime: hoursToHMS(+r.break_hours || 0),
        idleTime: hoursToHMS(+r.idle_hours || 0),
        meetingTime: hoursToHMS(+r.meeting_hours || 0),
        productivity: Math.min(100, +r.productivity || 0),
        date: r.date ? new Date(r.date).toISOString().split("T")[0] : "",
      }));

      setAllReports(flattened);
      setError("");
    } catch (err) {
      console.error(err);
      setError("Failed to load reports. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReports(); }, [user]);

  // const members = useMemo(() => {
  //   const unique = [];
  //   const seen = new Set();
  //   allReports.forEach(r => {
  //     if (!seen.has(r.user_id) && r.user_id !== user?.id) {
  //       seen.add(r.user_id);
  //       unique.push({ user_id: r.user_id, name: r.name });
  //     }
  //   });
  //   return unique.sort((a,b) => a.name.localeCompare(b.name));
  // }, [allReports, user?.id]);

const summaryReports = useMemo(() => {
  const latestMap = {};

  allReports.forEach(r => {
    if (r.user_id === user?.id) return;

    if (
      !latestMap[r.user_id] ||
      new Date(r.date) > new Date(latestMap[r.user_id].date)
    ) {
      latestMap[r.user_id] = r;
    }
  });

  return Object.values(latestMap).sort((a, b) =>
    a.name.localeCompare(b.name)
  );
}, [allReports, user?.id]);

  const employeeReports = useMemo(() => {
    if (!selectedEmployee) return [];
    return allReports
      .filter(r => r.user_id === selectedEmployee.user_id && r.user_id !== user?.id)
      .sort((a,b) => new Date(b.date) - new Date(a.date));
  }, [allReports, selectedEmployee, user?.id]);

  // const handleEmployeeSelect = (employee) => {
  //   navigate(`/dashboard/detailed-reports/${employee.user_id}`, {
  //     state: { employee }
  //   });
  // };



  // const handleBackToSummary = () => {
  //   setViewMode('summary');
  //   setSelectedEmployee(null);
  // };

  const stats = useMemo(() => {
    const reports = viewMode === 'summary' ? summaryReports : employeeReports;
    const totalSeconds = reports.reduce((acc,r) => acc + timeToSeconds(r.workedHours), 0);
    const productiveSeconds = reports.reduce((acc,r) => acc + getProductiveSeconds(r.workedHours, r.breakTime, r.idleTime), 0);
    const avgProductivity = reports.length ? Math.round(reports.reduce((sum,r)=>sum+r.productivity,0)/reports.length) : 0;
    const efficiency = totalSeconds ? Math.round((productiveSeconds/totalSeconds)*100) : 0;
    return {
      totalHours: `${Math.floor(totalSeconds/3600)}h ${Math.floor((totalSeconds%3600)/60)}m`,
      avgProductivity: `${avgProductivity}%`,
      efficiency: `${efficiency}%`,
      productiveTime: `${Math.floor(productiveSeconds/3600)}h ${Math.floor((productiveSeconds%3600)/60)}m`
    };
  }, [viewMode, summaryReports, employeeReports]);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;

  return (
    <>
      <Row>
        <Col sm={12}>
          <Stats stats={stats} teamCount={summaryReports.length} />
        </Col>
      </Row>

      <Row>
        <Col sm={12}>
          <PerformanceOfAgents 
            reports={summaryReports}
            onRowClick={(agent) =>
              navigate(`/dashboard/detailed-reports/${agent.user_id}`, {
                state: {
                  employee: agent,
                  reports: allReports.filter(r => r.user_id === agent.user_id)
                }
              })
            }

          />

        </Col>
      </Row>
    </>
  );
};

export default Reports;
